import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Category, ColorEnum } from '../api/categories';
import {
  useArchiveCategory,
  useCreateCategory,
  usePatchCategory,
} from '../api/categories';
import { ApiError } from '../api/client';
import { useProducts } from '../api/products';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';

const COLORS: ColorEnum[] = ['CORAL', 'TEAL', 'GREEN', 'LILAC', 'PURPLE'];

const COLOR_VAR: Record<ColorEnum, string> = {
  CORAL: 'var(--oeg-coral)',
  TEAL: 'var(--oeg-teal)',
  GREEN: 'var(--oeg-green)',
  LILAC: 'var(--oeg-lilac)',
  PURPLE: 'var(--oeg-purple)',
};

const categorySchema = z.object({
  name: z.string().min(1, 'Required').max(64, 'Max 64 characters'),
  color: z.enum(['CORAL', 'TEAL', 'GREEN', 'LILAC', 'PURPLE']),
});

type CategoryValues = z.infer<typeof categorySchema>;

export interface CategoryModalProps {
  eventId: string;
  mode: 'create' | 'edit';
  category: Category | null;
  onClose: () => void;
}

export function CategoryModal({ eventId, mode, category, onClose }: CategoryModalProps) {
  const createCategory = useCreateCategory(eventId);
  const patchCategory = usePatchCategory(eventId);
  const archiveCategory = useArchiveCategory(eventId);
  const productsQuery = useProducts(eventId);

  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CategoryValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: category?.name ?? '',
      color: category?.color ?? 'CORAL',
    },
  });

  const selectedColor = watch('color');

  const productsUsingCategory =
    category && productsQuery.data
      ? productsQuery.data.filter(
          (p) => p.categoryId === category.id && !p.archivedAt,
        ).length
      : 0;

  const onSubmit = handleSubmit(async (values) => {
    setTopLevelError(null);
    try {
      if (mode === 'create') {
        await createCategory.mutateAsync({
          name: values.name,
          color: values.color,
        });
      } else if (category) {
        await patchCategory.mutateAsync({
          id: category.id,
          body: { name: values.name, color: values.color },
        });
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate_name') {
        setTopLevelError('A category with that name already exists.');
      } else if (err instanceof ApiError && err.code === 'invalid_request') {
        setTopLevelError("Some fields aren't valid — check your input.");
      } else {
        setTopLevelError('Something went wrong. Try again.');
      }
    }
  });

  async function performArchive() {
    if (!category) return;
    try {
      await archiveCategory.mutateAsync(category.id);
      onClose();
    } catch {
      setTopLevelError('Could not archive. Try again.');
      setConfirmingArchive(false);
    }
  }

  function onArchiveClick() {
    if (!category) return;
    if (productsUsingCategory > 0) {
      setConfirmingArchive(true);
    } else {
      void performArchive();
    }
  }

  const title = mode === 'create' ? 'New category' : (category?.name ?? 'Edit category');
  const sub =
    mode === 'create'
      ? 'Goes live to floor devices on save'
      : `${productsUsingCategory} product${productsUsingCategory === 1 ? '' : 's'} use this category`;

  if (confirmingArchive && category) {
    return (
      <Modal
        title="Archive category?"
        onClose={() => setConfirmingArchive(false)}
        footer={
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setConfirmingArchive(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void performArchive()}
              data-testid="confirm-archive"
            >
              Archive
            </button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-1)' }}>
          {productsUsingCategory} product{productsUsingCategory === 1 ? '' : 's'} use this
          category — they&rsquo;ll stay, but won&rsquo;t appear on the floor catalog until
          re-categorized.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={title}
      sub={sub}
      onClose={onClose}
      footer={
        <>
          {mode === 'edit' && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginRight: 'auto', color: 'var(--oeg-danger)' }}
              onClick={onArchiveClick}
              data-testid="archive-button"
            >
              Archive
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Saving…'
              : mode === 'create'
                ? 'Create'
                : 'Save changes'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 16 }}>
        <Field label="Name" error={errors.name?.message}>
          <input type="text" maxLength={64} {...register('name')} />
        </Field>
        <Field label="Color" error={errors.color?.message}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {COLORS.map((c) => {
              const active = selectedColor === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  aria-pressed={active}
                  data-testid={`color-${c}`}
                  onClick={() => setValue('color', c, { shouldDirty: true })}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: COLOR_VAR[c],
                    border: active ? '2px solid var(--fg-1)' : '1px solid var(--border-1)',
                    boxShadow: active
                      ? '0 0 0 2px var(--bg-app), 0 0 0 4px var(--oeg-purple)'
                      : 'none',
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </div>
        </Field>
        <input type="hidden" {...register('color')} />
        {topLevelError && <div className="form-error">{topLevelError}</div>}
      </form>
    </Modal>
  );
}
